import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAward, FaCrown, FaGem, FaSearch, FaStar, FaUsers } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const CACHE_TTL = 60 * 60 * 1000;

const tierDefinitions = [
  {
    key: 'platinumVip',
    premium: 4,
    icon: FaGem,
    image: 'https://hub.mileageriot.com/assets/images/vip_4.png',
    className: 'is-platinum',
    titleKey: 'sponsors.platinumVip',
  },
  {
    key: 'goldVip',
    premium: 1,
    icon: FaCrown,
    image: 'https://hub.mileageriot.com/assets/images/vip_1.png',
    className: 'is-gold',
    titleKey: 'sponsors.goldVIP',
  },
  {
    key: 'silverVip',
    premium: 2,
    icon: FaStar,
    image: 'https://hub.mileageriot.com/assets/images/vip_2.png',
    className: 'is-silver',
    titleKey: 'sponsors.silverVIP',
  },
  {
    key: 'bronzeVip',
    premium: 3,
    icon: FaAward,
    image: 'https://hub.mileageriot.com/assets/images/vip_3.png',
    className: 'is-bronze',
    titleKey: 'sponsors.bronzeVIP',
  },
];

const emptyTiers = () => Object.fromEntries(tierDefinitions.map((tier) => [tier.key, []]));

const categorizeSponsors = (users) => {
  const categorized = emptyTiers();
  users.forEach((user) => {
    const tier = tierDefinitions.find((definition) => definition.premium === Number(user.premium));
    if (tier) categorized[tier.key].push(user);
  });
  return categorized;
};

const readPrerenderedSponsors = () => {
  if (typeof document === 'undefined') return null;
  const payload = document.getElementById('__SPONSORS_DATA__')?.textContent;
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

const Sponsors = () => {
  const { t, i18n } = useTranslation();
  const [initialSponsors] = useState(readPrerenderedSponsors);
  const [sponsors, setSponsors] = useState(() => initialSponsors || emptyTiers());
  const [loading, setLoading] = useState(() => !initialSponsors);
  const [error, setError] = useState(false);
  const [tierFilter, setTierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'ru-RU';

  useEffect(() => {
    if (initialSponsors) return undefined;

    const fetchSponsors = async () => {
      setLoading(true);
      setError(false);

      try {
        const cachedData = localStorage.getItem('sponsorsDataV2');
        const cachedTimestamp = Number(localStorage.getItem('sponsorsDataTimestampV2'));
        const isCacheValid = cachedData && cachedTimestamp && Date.now() - cachedTimestamp < CACHE_TTL;

        let categorized;
        if (isCacheValid) {
          categorized = JSON.parse(cachedData);
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/sponsors');
          if (!response.ok) throw new Error(`Sponsors request failed: ${response.status}`);
          const data = await response.json();
          if (!Array.isArray(data)) throw new Error('Sponsors response is not an array');
          categorized = categorizeSponsors(data);
          localStorage.setItem('sponsorsDataV2', JSON.stringify(categorized));
          localStorage.setItem('sponsorsDataTimestampV2', Date.now().toString());
        }

        setSponsors(categorized);
      } catch (fetchError) {
        console.error('Error fetching sponsors:', fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
    return undefined;
  }, [initialSponsors]);

  const totalSponsors = tierDefinitions.reduce((total, tier) => total + (sponsors[tier.key]?.length || 0), 0);

  const filteredTiers = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale);

    return tierDefinitions
      .filter((tier) => tierFilter === 'all' || tier.key === tierFilter)
      .map((tier) => ({
        ...tier,
        users: (sponsors[tier.key] || []).filter((user) => (
          !query || String(user.name || '').toLocaleLowerCase(locale).includes(query)
        )),
      }))
      .filter((tier) => tier.users.length > 0);
  }, [locale, searchQuery, sponsors, tierFilter]);

  const filteredCount = filteredTiers.reduce((total, tier) => total + tier.users.length, 0);

  const handleAvatarError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.style.display = 'none';
  };

  return (
    <>
      <Helmet>
        <title>Спонсоры — Mileage Riot ВТК | ETS2 & ATS</title>
        <meta name="description" content="Спонсоры и VIP-участники Mileage Riot, поддерживающие развитие виртуальной транспортной компании." />
        <link rel="canonical" href="https://mileageriot.com/sponsors" />
        <meta property="og:title" content="Спонсоры — Mileage Riot" />
        <meta property="og:description" content="VIP-участники и спонсоры Mileage Riot." />
        <meta property="og:url" content="https://mileageriot.com/sponsors" />
        <meta property="og:locale" content={locale === 'en-GB' ? 'en_GB' : 'ru_RU'} />
      </Helmet>

      <main className="sponsors-page-upgraded page-sponsors">
        <div className="sponsors-page-grid-bg" aria-hidden="true"></div>
        <div className="sponsors-page-glow sponsors-page-glow-left" aria-hidden="true"></div>
        <div className="sponsors-page-glow sponsors-page-glow-right" aria-hidden="true"></div>

        <div className="container sponsors-page-container">
          <header className="sponsors-page-hero">
            <div className="sponsors-page-hero-copy">
              <div className="section-badge sponsors-page-badge">
                <FaCrown aria-hidden="true" />
                <span>{t('sponsors.badge')}</span>
              </div>
              <h1>{t('sponsors.title')}</h1>
              <p>{t('sponsors.subtitle')}</p>
            </div>

            <div className="sponsors-page-summary" aria-label={t('sponsors.summaryLabel')}>
              <div>
                <FaUsers aria-hidden="true" />
                <span>{t('sponsors.totalSupporters')}</span>
                <strong>{loading ? '—' : totalSponsors}</strong>
              </div>
              <div>
                <FaGem aria-hidden="true" />
                <span>{t('sponsors.vipLevels')}</span>
                <strong>4</strong>
              </div>
            </div>
          </header>

          <section className="sponsors-tier-overview" aria-label={t('sponsors.vipLevels')}>
            {tierDefinitions.map((tier) => {
              const Icon = tier.icon;
              const count = sponsors[tier.key]?.length || 0;
              return (
                <button
                  type="button"
                  className={`sponsors-tier-card ${tier.className}${tierFilter === tier.key ? ' active' : ''}`}
                  key={tier.key}
                  onClick={() => setTierFilter((current) => current === tier.key ? 'all' : tier.key)}
                  aria-pressed={tierFilter === tier.key}
                >
                  <span className="sponsors-tier-icon"><Icon aria-hidden="true" /></span>
                  <div><small>{t(tier.titleKey)}</small><strong>{loading ? '—' : count}</strong></div>
                  <span className="sponsors-tier-view">{t('sponsors.viewTier')}</span>
                </button>
              );
            })}
          </section>

          <div className="sponsors-toolbar">
            <label className="sponsors-search">
              <FaSearch aria-hidden="true" />
              <span className="visually-hidden">{t('sponsors.searchLabel')}</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('sponsors.searchPlaceholder')}
              />
            </label>

            <div className="sponsors-filter-buttons" role="group" aria-label={t('sponsors.filterLabel')}>
              <button type="button" className={tierFilter === 'all' ? 'active' : ''} onClick={() => setTierFilter('all')} aria-pressed={tierFilter === 'all'}>
                {t('sponsors.allLevels')}
              </button>
              {tierDefinitions.map((tier) => (
                <button type="button" className={tierFilter === tier.key ? `active ${tier.className}` : tier.className} onClick={() => setTierFilter(tier.key)} aria-pressed={tierFilter === tier.key} key={tier.key}>
                  {t(tier.titleKey).replace(/\s*VIP$/i, '')}
                </button>
              ))}
            </div>

            <span className="sponsors-results-count">{t('sponsors.found', { count: filteredCount })}</span>
          </div>

          {loading ? (
            <div className="sponsors-page-state" role="status">
              <span className="sponsors-page-loader" aria-hidden="true"></span>
              <p>{t('sponsors.loading')}</p>
            </div>
          ) : error ? (
            <div className="sponsors-page-state" role="alert">
              <FaCrown aria-hidden="true" />
              <h2>{t('sponsors.error')}</h2>
              <p>{t('sponsors.errorHint')}</p>
            </div>
          ) : filteredTiers.length === 0 ? (
            <div className="sponsors-page-state">
              <FaSearch aria-hidden="true" />
              <h2>{t('sponsors.noResults')}</h2>
              <p>{t('sponsors.noResultsHint')}</p>
            </div>
          ) : (
            <div className="sponsors-tier-sections" aria-live="polite">
              {filteredTiers.map((tier) => {
                const Icon = tier.icon;
                return (
                  <section className={`sponsors-vip-section ${tier.className}`} key={tier.key}>
                    <header className="sponsors-vip-heading">
                      <div className="sponsors-vip-title">
                        <span><Icon aria-hidden="true" /></span>
                        <div><small>{t('sponsors.supportLevel')}</small><h2>{t(tier.titleKey)}</h2></div>
                      </div>
                      <div className="sponsors-vip-count"><strong>{tier.users.length}</strong><span>{t('sponsors.supporters')}</span></div>
                    </header>

                    <div className="sponsors-members-grid">
                      {tier.users.map((user, index) => (
                        <article className="sponsor-member-card" key={user.id}>
                          <div className="sponsor-member-top">
                            <span>{String(index + 1).padStart(2, '0')}</span>
                            <img src={tier.image} alt="" aria-hidden="true" />
                          </div>
                          <div className="sponsor-avatar-shell">
                            <span aria-hidden="true">
                              {user.name?.replace(/^[^\p{L}\p{N}]+/u, '').charAt(0) || 'M'}
                            </span>
                            <img
                              src={user.avatar}
                              alt={user.name}
                              loading="lazy"
                              decoding="async"
                              onError={handleAvatarError}
                            />
                          </div>
                          <div className="sponsor-member-copy">
                            <small>{t(tier.titleKey)}</small>
                            <h3 title={user.name}>{user.name}</h3>
                          </div>
                          <div className="sponsor-member-footer" aria-hidden="true">
                            <span></span>
                            <span>{t('sponsors.thankYou')}</span>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default Sponsors;
